import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Jp } from '@/lib/jaText';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useEarlyAccessCounts } from '@/lib/useEarlyAccessCounts';
import { metaTrack } from '@/lib/metaPixel';
import { recordAttribution } from '@/lib/attribution';
import {
  Sparkles, User, Building2, Shield, Mail, Check, ShieldCheck,
  Clock, ChevronDown, Loader2, Lock, ArrowRight, Crown, Zap, CalendarClock,
} from 'lucide-react';
import { useLaunchCountdown, LAUNCH_LABEL } from '@/lib/useLaunchCountdown';

const API = process.env.REACT_APP_BACKEND_URL;

const TRACKS = [
  {
    key: 'personal', Icon: User,
    ja: ' 個人', en: 'Personal',
    accent: 'text-emerald-700', ring: 'ring-emerald-500', chip: 'bg-emerald-500',
  },
  {
    key: 'business', Icon: Building2,
    ja: '法人', en: 'Business',
    accent: 'text-fuchsia-700', ring: 'ring-fuchsia-500', chip: 'bg-fuchsia-500',
  },
  {
    key: 'government', Icon: Shield,
    ja: '官公庁・公共部門', en: 'Government',
    accent: 'text-indigo-700', ring: 'ring-indigo-500', chip: 'bg-indigo-500',
  },
];

const TRACK_LABEL = {
  personal: { ja: '個人向け', en: 'Personal' },
  business: { ja: '法人向け', en: 'Business' },
  government: { ja: '政府・米軍関係機関向け', en: 'Government & Public-Sector' },
};

const FAQ = [
  {
    ja: { q: '登録すればアカウントは確保されますか？', a: '先行受付リストの先着100,000名様は、アカウント有効化の開始時に優先的にご案内します。ローンチ手続きの際に、ご自身でアカウントを有効化していただく必要があります。' },
    en: { q: 'Does joining the Early Access list guarantee an account?', a: 'The first 100,000 people on the Early Access list receive priority access when account activation opens. You will still need to activate your account during the launch process.' },
  },
  {
    ja: { q: 'アカウント種別は後で変更できますか？', a: 'はい。いま選んだアカウント種別はオンボーディング準備のためのものです。アカウント有効化時に変更できます。' },
    en: { q: 'Can I change my account type later?', a: 'Yes. The account type you choose now helps us prepare onboarding. You can change it when your account is activated.' },
  },
  {
    ja: { q: '有効化が始まると何が起きますか？', a: '先行受付リストのメンバーは、一般公開より前に、1年間Premium Trialアカウントを優先的に取得できます。' },
    en: { q: 'What happens when activation opens?', a: 'Early Access list members receive first access to claim a 1-Year Premium Trial account before general public signup.' },
  },
  {
    ja: { q: '料金はかかりますか？', a: 'リストへの登録は無料です。有効化時には1年間のPremium Trial（無料）をご案内します。' },
    en: { q: 'Does it cost anything?', a: 'Joining the list is free. At activation you are offered a 1-Year Premium Trial at no cost.' },
  },
  {
    ja: { q: 'Japan Toll Receipts は公式機関ですか？', a: 'いいえ。当社は独立したサービスであり、NEXCO・ETC利用照会サービス・道路事業者ではありません。' },
    en: { q: 'Is Japan Toll Receipts official?', a: 'No. We are an independent service and are not NEXCO, the ETC Usage Inquiry Service (ETC利用照会サービス), or a toll road operator.' },
  },
  {
    ja: { q: 'いつでも配信停止できますか？', a: 'はい。すべてのメールに配信停止リンクが含まれています。いつでも解除できます。' },
    en: { q: 'Can I unsubscribe anytime?', a: 'Yes. Every email includes an unsubscribe link. You can opt out at any time.' },
  },
];

/* ── Compact, accessible segmented control for account-type interest ── */
function TrackSelector({ track, setTrack, ja }) {
  const onKey = (e) => {
    const idx = TRACKS.findIndex((t) => t.key === track);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setTrack(TRACKS[(idx + 1) % TRACKS.length].key);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      setTrack(TRACKS[(idx - 1 + TRACKS.length) % TRACKS.length].key);
    }
  };
  return (
    <div
      role="radiogroup"
      aria-label={ja ? 'ご関心のあるアカウントタイプ' : 'Account type you are interested in'}
      onKeyDown={onKey}
      className="grid grid-cols-3 gap-1.5 p-1 rounded-xl bg-slate-100"
      data-testid="ea-track-selector"
    >
      {TRACKS.map((t) => {
        const selected = track === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => setTrack(t.key)}
            data-testid={`ea-track-${t.key}`}
            className={`flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1.5 sm:px-2 py-2 text-[11px] sm:text-[13px] font-bold leading-tight transition-all ${
              selected
                ? `bg-white shadow-sm ring-2 ${t.ring} ${t.accent}`
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.Icon className="hidden sm:block w-3.5 h-3.5 shrink-0" strokeWidth={2.4} />
            <span className="text-center">{ja ? t.ja : t.en}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function EarlyAccessLandingPage() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const counts = useEarlyAccessCounts();
  const { days, hours, minutes } = useLaunchCountdown(60000);

  const [track, setTrack] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('type');
    return ['personal', 'business', 'government'].includes(t) ? t : 'personal';
  });
  const [form, setForm] = useState({
    email: '', name: '', organization_name: '', role_title: '',
    country_region: '', signup_note: '', estimated_etc_card_count: '',
  });
  const [prefLang, setPrefLang] = useState(lang === 'ja' ? 'ja' : 'en');
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [openFaq, setOpenFaq] = useState([0, 1, 2]);
  const honeypot = useRef('');

  const isBusinessLike = track === 'business' || track === 'government';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.email.includes('@')) {
      setError(ja ? '有効なメールアドレスを入力してください。' : 'Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const res = await fetch(`${API}/api/early-access/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          account_type: track,
          preferred_language: prefLang,
          name: form.name,
          organization_name: form.organization_name,
          role_title: form.role_title,
          country_region: form.country_region,
          signup_note: form.signup_note,
          estimated_etc_card_count: form.estimated_etc_card_count ? Number(form.estimated_etc_card_count) : null,
          consent_email_updates: true,
          privacy_acknowledged: true,
          source_url: window.location.href,
          source_page_type: 'early_access_landing',
          utm_source: params.get('utm_source') || '',
          utm_medium: params.get('utm_medium') || '',
          utm_campaign: params.get('utm_campaign') || '',
          company_website: honeypot.current,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || (ja ? '登録に失敗しました。' : 'Something went wrong.'));
        return;
      }
      setSuccess(data);
      try { metaTrack.lead({ email: form.email.trim() }, { account_type: track, source: 'early_access_landing' }); } catch { /* noop */ }
      try { recordAttribution('waitlist', { account_type: track }); } catch { /* noop */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try { localStorage.setItem('ea_joined', '1'); } catch { /* noop */ }
    } catch (err) {
      setError(err.message || (ja ? 'ネットワークエラー。' : 'Network error.'));
    } finally {
      setLoading(false);
    }
  };

  const total = counts;
  const joined = total.claimed || 0;
  const remaining = total.remaining != null ? total.remaining : 100000;
  const limit = total.limit || 100000;
  const full = total.full;
  const fmt = (n) => Number(n || 0).toLocaleString();

  return (
    <div className="min-h-screen bg-[#0f0a1f] text-white" data-testid="early-access-page">
      <Header />
      <main id="main-content">
        {success ? (
          <SuccessPanel ja={ja} data={success} track={track} />
        ) : (
          <>
            {/* ── COMPACT ABOVE-THE-FOLD HERO + SIGNUP ─────────────── */}
            <section className="relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(168,85,247,0.28),transparent_55%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(16,185,129,0.16),transparent_55%)]" />
              <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-10 lg:pt-12 lg:pb-14 grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
                {/* LEFT — pitch (compact) */}
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-purple-400/40 bg-purple-500/10 px-3.5 py-1 text-xs font-semibold text-purple-200">
                    <Sparkles className="w-3.5 h-3.5" /> {ja ? '先行受付プログラム' : 'Early Access Program'}
                  </span>
                  <h1 className="mt-3 text-[26px] sm:text-4xl lg:text-[44px] font-extrabold leading-[1.12] tracking-tight [text-wrap:balance]">
                    {ja ? '先行受付リストに登録。' : 'Join the Early Access list.'}
                    <span className="block bg-gradient-to-r from-emerald-300 via-purple-300 to-fuchsia-300 bg-clip-text text-transparent">
                      {ja ? 'アカウント有効化を優先案内。' : 'Get first access when activation opens.'}
                    </span>
                  </h1>
                  <p className="mt-3 text-sm sm:text-base text-purple-100/80 max-w-xl leading-relaxed">
                    {ja
                      ? <Jp>先着100,000名様は、ローンチ開始時に1年間Premium Trialアカウントを優先取得いただけます。</Jp>
                      : 'The first 100,000 on the list get priority access to claim a 1-Year Premium Trial account when launch begins.'}
                  </p>
                </div>

                {/* RIGHT — HIGH-CONTRAST signup card (stands out on dark page) */}
                <div className="relative" data-testid="ea-signup-card">
                  <div className="absolute -inset-1 rounded-[26px] bg-gradient-to-br from-emerald-400 via-purple-500 to-fuchsia-500 opacity-70 blur-lg" aria-hidden="true" />
                  <form onSubmit={submit} className="relative rounded-3xl bg-white text-slate-900 p-5 sm:p-7 shadow-2xl ring-1 ring-black/5 jtr-reveal" data-testid="ea-form">
                    {/* Honeypot */}
                    <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
                      <label>Company website<input type="text" tabIndex={-1} autoComplete="off"
                        onChange={(e) => { honeypot.current = e.target.value; }} /></label>
                    </div>

                    {/* Live list counter strip — kept inside the card so it's
                        adjacent to the form on both desktop and mobile. */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 mb-4" data-testid="ea-combined-counter">
                      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        </span>
                        {ja ? '先行受付リスト' : 'Early Access list'}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-base font-black tabular-nums text-slate-900" data-testid="ea-combined-joined">{fmt(joined)}</span>
                        <span className="text-[11px] text-slate-500">{ja ? '登録済み' : 'joined'}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-base font-black tabular-nums text-emerald-700" data-testid="ea-combined-remaining">{fmt(remaining)}</span>
                        <span className="text-[11px] text-slate-500">/ {fmt(limit)} {ja ? '残り' : 'left'}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white shadow">
                        <Crown className="w-4 h-4" />
                      </span>
                      <div>
                        <p className="text-[15px] font-extrabold leading-tight">{ja ? 'メールで枠を確保' : 'Reserve your spot'}</p>
                        <p className="text-[11px] text-slate-500 leading-tight">{ja ? '無料・約30秒・カード不要' : 'Free · ~30 seconds · no card'}</p>
                      </div>
                    </div>

                    {/* Account type — compact segmented pills */}
                    <div className="mt-4">
                      <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                        {ja ? 'ご関心のあるタイプ' : "I'm interested in"}
                      </label>
                      <TrackSelector track={track} setTrack={setTrack} ja={ja} />
                    </div>

                    {/* Email + submit (inline on desktop) */}
                    <div className="mt-4">
                      <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                        {ja ? 'メールアドレス' : 'Email address'}
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="email" required value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="you@example.com" autoComplete="email"
                            data-testid="ea-email-input"
                            className="w-full rounded-xl bg-slate-50 border border-slate-300 pl-10 pr-3 py-3 text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                          />
                        </div>
                        <button type="submit" disabled={loading}
                          data-testid="ea-submit-btn"
                          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-5 py-3 text-sm font-extrabold text-white shadow-lg hover:from-purple-500 hover:to-fuchsia-500 disabled:opacity-60 transition-all jtr-cta-sheen">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          {full ? (ja ? '順番待ちに登録' : 'Join waitlist') : (ja ? '先行受付に登録' : 'Join Early Access')}
                        </button>
                      </div>
                    </div>

                    {error && (
                      <p className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[13px] text-rose-700" data-testid="ea-error">{error}</p>
                    )}

                    {/* Optional progressive details (collapsed — never pushes email down) */}
                    <button type="button" onClick={() => setShowMore((v) => !v)}
                      data-testid="ea-more-toggle"
                      className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-purple-700 hover:text-purple-900">
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
                      {ja ? '詳細を追加（任意）' : 'Add more details (optional)'}
                    </button>
                    {showMore && (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="ea-more-fields">
                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder={ja ? 'お名前' : 'Full name'} data-testid="ea-name-input"
                          className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                        <input type="text" value={form.country_region} onChange={(e) => setForm({ ...form, country_region: e.target.value })}
                          placeholder={ja ? '国・地域（例：東京）' : 'Country / region'} data-testid="ea-region-input"
                          className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                        <input type="number" min="0" value={form.estimated_etc_card_count} onChange={(e) => setForm({ ...form, estimated_etc_card_count: e.target.value })}
                          placeholder={ja ? 'ETCカード枚数の目安' : 'Estimated ETC cards'} data-testid="ea-cards-input"
                          className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                        <select value={prefLang} onChange={(e) => setPrefLang(e.target.value)}
                          data-testid="ea-language-select"
                          className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:bg-white focus:border-purple-500 focus:outline-none">
                          <option value="ja">日本語 (Japanese)</option>
                          <option value="en">English</option>
                        </select>
                        {isBusinessLike && (
                          <>
                            <input type="text" value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                              placeholder={ja ? '組織名' : 'Organization'} data-testid="ea-org-input"
                              className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                            <input type="text" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })}
                              placeholder={ja ? '役職' : 'Role / title'} data-testid="ea-role-input"
                              className="rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none" />
                          </>
                        )}
                        <textarea rows={2} value={form.signup_note} onChange={(e) => setForm({ ...form, signup_note: e.target.value })}
                          placeholder={ja ? 'ご要望・メモ' : 'Notes'} data-testid="ea-note-input"
                          className="sm:col-span-2 rounded-lg bg-slate-50 border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:border-purple-500 focus:outline-none resize-none" />
                      </div>
                    )}

                    {/* Compact trust note (acknowledgement included by joining) */}
                    <p className="mt-3 text-[11px] text-slate-500 leading-snug" data-testid="ea-trust-note">
                      {ja
                        ? '登録により、先行受付の更新メール受信と、本サービスがNEXCO・ETC利用照会サービスとは無関係の独立サービスであることに同意したものとみなされます。いつでも配信停止できます。'
                        : 'By joining you agree to receive Early Access update emails and acknowledge that Japan Toll Receipts is independent and not NEXCO or the ETC Usage Inquiry Service (ETC利用照会サービス). Unsubscribe anytime.'}
                    </p>
                    <p className="mt-2 text-[11px] text-slate-400 leading-snug" data-testid="ea-no-sensitive-note">
                      {ja
                        ? 'ETCカード番号・車載器（OBU）番号・ETC利用照会サービスの認証情報・走行データは入力しないでください。'
                        : 'Please do not enter ETC card numbers, OBU numbers, ETC Usage Inquiry Service (ETC利用照会サービス) credentials, or toll-usage data.'}
                    </p>
                  </form>
                </div>
              </div>
            </section>

            {/* ── PRIORITY ACCESS EXPLAINED ────────────────────────── */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8" data-testid="ea-priority-explainer">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-4 h-4 text-emerald-300" />
                  <h2 className="text-base md:text-lg font-bold text-white">
                    {ja ? '優先アクセスの仕組み' : 'How priority access works'}
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { Icon: Mail, ja: ['1. リストに登録', 'いまメールで先行受付リストに登録します。'], en: ['1. Join the list', 'Sign up now with your email to join the Early Access list.'] },
                    { Icon: Clock, ja: ['2. 優先案内ウィンドウ', 'リストメンバーは一般公開より前に優先的にご案内します。'], en: ['2. Priority window', 'List members get a priority window before general public signup.'] },
                    { Icon: Crown, ja: ['3. 先着100,000名', '先着100,000名様がローンチ時にアカウントを優先取得できます。'], en: ['3. First 100,000', 'The first 100,000 on the list get first access to claim accounts at launch.'] },
                    { Icon: ShieldCheck, ja: ['4. 1年間Premium Trial', '有効化時に1年間Premium Trial（無料）をご案内します。'], en: ['4. 1-Year Premium Trial', 'At activation you claim a 1-Year Premium Trial — free.'] },
                  ].map((s, i) => (
                    <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                      <s.Icon className="w-5 h-5 text-purple-300 mb-2" strokeWidth={2.2} />
                      <p className="text-[13px] font-bold text-white">{ja ? s.ja[0] : s.en[0]}</p>
                      <p className="mt-1 text-[12px] text-purple-100/65 leading-relaxed">{ja ? s.ja[1] : s.en[1]}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[12px] text-purple-100/60 leading-relaxed" data-testid="ea-account-type-note">
                  {ja
                    ? '選択したアカウント種別はオンボーディング準備のためのものです。アカウント有効化時に変更できます。'
                    : 'Your selected account type helps us prepare onboarding. You can change it later when your account is activated.'}
                </p>

                {/* Slim launch countdown */}
                <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-purple-100/70" data-testid="ea-launch-countdown">
                  <CalendarClock className="w-4 h-4 text-purple-300 shrink-0" />
                  <span>{ja ? `公開予定日 · ${LAUNCH_LABEL.ja}` : `Estimated launch · ${LAUNCH_LABEL.en}`}</span>
                  <span className="font-mono tabular-nums text-white/85">{days}d {hours}h {minutes}m</span>
                </div>
              </div>
            </section>

            {/* ── WHAT EACH TYPE GETS DURING THE TRIAL ─────────────── */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
              <h2 className="text-base md:text-lg font-bold text-white mb-4">
                {ja ? '1年間Premium Trialで使える機能' : 'What each account type gets during the 1-Year Premium Trial'}
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { key: 'personal', Icon: User, accent: 'from-emerald-500 to-teal-600',
                    ja: ['個人向け', ['毎日・毎週・毎月の自動レポート', 'ETCカード最大10枚', '家族の車両リスト・走行記録の名前付け', 'PDF＋CSV添付']],
                    en: ['Personal', ['Daily / weekly / monthly auto-reports', 'Up to 10 ETC cards', 'Family vehicle list & trip labels', 'PDF + CSV attachments']] },
                  { key: 'business', Icon: Building2, accent: 'from-fuchsia-500 to-purple-600',
                    ja: ['法人向け', ['車両全体の一括レポート', 'サブアカウント・役割別権限', 'カードグループ・部門別集計', '登録車両リスト＋目的外利用検知']],
                    en: ['Business', ['Bulk fleet reports', 'Sub-accounts & role-based access', 'Card groups & department roll-ups', 'Registered Vehicle Registry + misuse review']] },
                  { key: 'government', Icon: Shield, accent: 'from-indigo-500 to-blue-600',
                    ja: ['政府・米軍関係機関向け', ['Business Suiteと同等の全機能', '確認対応エクスポート', '厳格なアクセス管理', 'ナンバー分類認識']],
                    en: ['Government & Public-Sector', ['Full Business Suite features', 'Exports organized for review', 'Strict access controls', 'Plate-family recognition']] },
                ].map((c) => (
                  <div key={c.key} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`ea-track-benefits-${c.key}`}>
                    <div className={`flex items-center gap-2 bg-gradient-to-r ${c.accent} px-4 py-3`}>
                      <c.Icon className="w-4 h-4 text-white" />
                      <span className="text-sm font-bold text-white">{ja ? c.ja[0] : c.en[0]}</span>
                    </div>
                    <ul className="p-4 space-y-2">
                      {(ja ? c.ja[1] : c.en[1]).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-purple-100/80">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-300 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[12px] text-purple-100/55 leading-relaxed" data-testid="ea-locked-pricing-note">
                {ja
                  ? '体験終了後は、先行受付価格をご利用いただけます（料金は追跡するETCカード枚数に基づきます）。年額プランや5年パスはありません。'
                  : 'After the trial, you keep Early Access pricing (based on the number of ETC cards you track). No annual plan or 5-Year Pass.'}
              </p>
            </section>

            {/* ── FAQ ──────────────────────────────────────────────── */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
              <h2 className="text-base md:text-lg font-bold text-center mb-5">{ja ? 'よくあるご質問' : 'Frequently asked questions'}</h2>
              <div className="space-y-2">
                {FAQ.map((f, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
                    <button type="button" onClick={() => setOpenFaq((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i])}
                      data-testid={`ea-faq-${i}`}
                      className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold text-purple-50 hover:bg-white/[0.04]">
                      {ja ? f.ja.q : f.en.q}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openFaq.includes(i) ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq.includes(i) && (
                      <p className="px-5 pb-4 text-sm text-purple-100/70 leading-relaxed">{ja ? f.ja.a : f.en.a}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-2.5" data-testid="ea-manage-prefs">
                <p className="text-sm text-purple-100/60">
                  {ja ? 'すでに登録済みですか？' : 'Already on the list?'}
                </p>
                <Link
                  to={ja ? '/ja/email/preferences' : '/en/email/preferences'}
                  data-testid="ea-unsubscribe-link"
                  className="inline-flex items-center justify-center text-center px-5 py-2.5 rounded-full border border-white/15 bg-white/[0.04] text-sm font-semibold text-purple-200 hover:bg-white/[0.08] hover:text-white transition-colors max-w-full"
                >
                  {ja ? 'メール配信設定の管理・配信停止' : 'Manage email preferences or unsubscribe'}
                </Link>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function SuccessPanel({ ja, data, track }) {
  const trackLabel = TRACK_LABEL[track] ? (ja ? TRACK_LABEL[track].ja : TRACK_LABEL[track].en) : '';
  const full = data.status === 'overflow_waitlist' || data.is_overflow;
  return (
    <section className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="ea-success">
      <div className="rounded-3xl border border-emerald-400/30 bg-emerald-500/[0.06] p-8 sm:p-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 mb-5">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-extrabold">{ja ? 'メールをご確認ください' : 'Check your email'}</h2>
        <p className="mt-3 text-purple-100/80 leading-relaxed">
          {ja
            ? `${trackLabel}の関心で先行受付リストに登録するため、確認メールを送信しました。メール内のリンクをクリックして登録を完了してください。`
            : `We sent a confirmation email to add you to the Early Access list (${trackLabel} interest). Click the link inside to complete your sign-up.`}
        </p>
        <p className="mt-3 text-sm text-purple-100/65 leading-relaxed">
          {ja
            ? 'リストの先着100,000名様は、ローンチ時に1年間Premium Trialアカウントを優先的に取得いただけます。'
            : 'The first 100,000 people on the list receive priority access to claim a 1-Year Premium Trial account at launch.'}
        </p>
        {full && (
          <p className="mt-4 inline-block rounded-lg bg-amber-500/15 border border-amber-400/30 px-4 py-2 text-sm text-amber-200">
            {ja ? 'リストは現在満員のため、順番待ちリストに登録されます。' : 'The list is currently full — you will join the overflow list.'}
          </p>
        )}
        {data.tracking_id && (
          <div className="mt-6" data-testid="ea-tracking-id">
            <p className="text-xs uppercase tracking-[0.18em] text-emerald-300/80 font-bold">
              {ja ? '先行受付追跡番号' : 'Your Early Access tracking number'}
            </p>
            <p className="mt-1 font-mono text-2xl font-black text-white tracking-wider">{data.tracking_id}</p>
            <p className="mt-1 text-xs text-purple-100/50">
              {ja ? 'この番号は確認メールにも記載されています。' : 'This number is also included in your confirmation email.'}
            </p>
          </div>
        )}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-emerald-200">
          <Check className="w-4 h-4" /> {ja ? '迷惑メールフォルダもご確認ください。' : 'Don’t forget to check your spam folder.'}
        </div>
        <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] px-4 py-3 text-left" data-testid="ea-success-policy">
          <p className="text-sm text-amber-100/90 leading-relaxed">
            {ja
              ? '⏳ 確認リンクは14日以内にクリックしてください。期限内に確認されない場合、メールアドレスはリストから自動的に削除されます（その場合は再度ご登録ください）。メールアドレスが無効でメールが届かない場合も、自動的に削除されます。'
              : '⏳ Please click the confirmation link within 14 days. If it isn’t confirmed in time, your email is automatically removed from the list — just sign up again if that happens. Invalid email addresses that bounce are also removed automatically.'}
          </p>
        </div>
        <p className="mt-5 text-xs text-purple-100/50" data-testid="ea-success-manage">
          {ja ? '気が変わりましたか？ ' : 'Changed your mind? '}
          <Link
            to={ja ? '/ja/email/preferences' : '/en/email/preferences'}
            data-testid="ea-success-unsubscribe-link"
            className="underline underline-offset-2 hover:text-purple-200 transition-colors"
          >
            {ja ? 'こちらから配信停止' : 'Unsubscribe here'}
          </Link>
        </p>
      </div>
    </section>
  );
}
